// Contact Form Content
export const contactFormSection = {
  headline: "Get in touch",
  subheadline: "Book a demo or ask us anything. We'll get back to you within 24 hours.",
  formFields: {
    name: {
      label: "Full Name",
      placeholder: "John Smith"
    },
    email: {
      label: "Email Address",
      placeholder: "john@company.com"
    },
    company: {
      label: "Company / Practice Name",
      placeholder: "Your Design Studio"
    },
    role: {
      label: "Your Role",
      placeholder: "Select your role",
      options: [
        "Architect",
        "Interior Designer",
        "Design Consultant",
        "Studio Owner",
        "Other"
      ]
    },
    message: {
      label: "Message",
      placeholder: "Tell us about your practice and what you're looking for..."
    }
  },
  submitButton: "Send Message",
  successMessage: "Thank you! We'll be in touch within 24 hours.",
  errorMessage: "Something went wrong. Please try again or email us directly."
};

// ============================================
// PRACTICIO LANDING PAGE CONTENT
// Edit this file to update all page content
// ============================================

export const heroContent = {
  headline: "Your projects deserve better than WhatsApp threads and Excel BOQs.",
  subheadline: "Practicio is a practice management platform built for architects and interior designers in India — client approvals, BOQ tracking, and billing, all in one place.",
  primaryCTA: "Book a Demo",
  secondaryCTA: "See How It Works",
  floatingBadge: {
    text: "",
    stat: "",
    statLabel: ""
  },
  showHeroImage: true // Set to true to show product mockup
};

export const problemSection = {
  headline: "Design work shouldn't feel this messy",
  subheadline: "",
  problems: [
    {
      title: "Approvals lost in WhatsApp",
      description: "Client decisions get made over calls, messages, and site visits — then disputed three months later. There's no record, no timestamp, no proof."
    },
    {
      title: "BOQ revisions with no trail",
      description: "The client changes the flooring. Then the lighting. Then comes back to the original flooring. Every revision is a new Excel file, a new email, a new argument. The real cost of the project becomes impossible to track."
    },
    {
      title: '"Approved" that gets un-approved',
      description: "Verbal confirmations get questioned once the bill arrives. Without a formal sign-off, you're always on the back foot defending decisions you already made."
    },
    {
      title: "Scope creep with no accountability",
      description: "Small changes pile up quietly — each one reasonable on its own, collectively a project that's grown 40% with no corresponding adjustment to fees."
    },
    {
      title: "Billing conversations feel personal",
      description: "Without milestones tied to clear deliverables and approvals, chasing payments feels like asking a favour. It shouldn't."
    },
    {
      title: "Hours lost reconstructing history",
      description: "When a client questions a decision from two months ago, you spend half a day digging through threads to prove what you already know."
    }
  ]
};

export const solutionSection = {
  headline: "One place where your projects stay in control",
  subheadline: "Practicio sits between you and your client — bringing structure to approvals, BOQ revisions, and billing without adding complexity to how you already work. No steep learning curve. No training decks. Most studios are running their first project on it within a day.",
  benefits: [
    {
      title: "Centralized client approvals",
      description: "Every design decision — layouts, materials, finishes — tracked with clear timestamps and sign-off status."
    },
    {
      title: "Automatic scope change history",
      description: "Revisions are logged with before/after states, so there's never a dispute about what changed or when."
    },
    {
      title: "Payment milestones linked to deliverables",
      description: "Billing stages tied directly to approvals and project progress, making payment conversations professional and clear."
    }
  ],
  mockupData: {
    projectName: "Bandra Residence Renovation",
    clientName: "Sharma Family",
    progress: 67,
    approvedItems: 8,
    pendingItems: 3,
    revisedItems: 1
  }
};

export const featuresSection = {
  headline: "Everything you need to stay in control",
  subheadline: "Professional tools built for how architects and interior designers actually work.",
  features: [
    {
      title: "Client Decision Tracking",
      description: "Track every design decision - layouts, materials, finishes, and budgets—with clear, timestamped approval status."
    },
    {
      title: "Scope Change History",
      description: "Every revision is automatically logged with a clear summary, previous state, and change timeline - no disputes later."
    },
    {
      title: "Client Approval Portal",
      description: "Clients approve decisions through a simple, mobile-friendly link—no apps, no accounts, no confusion."
    },
    {
      title: "Payment Milestones",
      description: "Tie approvals directly to payment stages, amounts, and reminders—keeping finances aligned with progress."
    },
    {
      title: "Project Visibility at a Glance",
      description: "Instantly see what is approved, what is pending, what is delayed, and what needs attention - across all projects."
    },
    {
      title: "BOQ & Specification Tracking",
      description: "Track every item — materials, finishes, furniture, fixtures — with quantities, costs, and vendor details. When something changes mid-project, the revision is logged and the cost delta updates immediately."
    }
  ]
};

export const pricingSection = {
  headline: "Simple, transparent pricing",
  subheadline: "Start free. Upgrade when you're ready.",
  plans: [
    {
      name: "Free",
      monthlyPrice: "₹0",
      yearlyPrice: "₹0",
      period: "",
      description: "Try Practicio on a single project",
      cta: "Get Started",
      popular: false
    },
    {
      name: "Studio",
      monthlyPrice: "₹2,998",
      yearlyPrice: "₹28,999",
      monthlyPeriod: "/month",
      yearlyPeriod: "/year",
      description: "Full platform for teams up to 10",
      cta: "Start Free Trial",
      popular: true
    }
  ],
  features: [
    { label: "Projects", free: "1", studio: "Unlimited" },
    { label: "Proposals", free: "3", studio: "Unlimited" },
    { label: "Team Members", free: "1", studio: "10" },
    { label: "Invite Users", free: false, studio: true },
    { label: "RFQ / Quotations", free: false, studio: true },
    { label: "PDF Generation", free: false, studio: true },
    { label: "Proposal Versioning", free: false, studio: true },
    { label: "Change Orders (VO)", free: false, studio: true },
    { label: "Client Portal", free: false, studio: true },
    { label: "BOQ Tracking", free: false, studio: true },
    { label: "Audit Log", free: false, studio: true },
    { label: "Watermark on exports", free: "Yes", studio: "No" }
  ]
};

export const audienceSection = {
  headline: "Built for professional design practices",
  subheadline: "From solo practices to studios — built for the way design work actually gets done in India.",
  audiences: [
    {
      title: "Architects",
      description: "Managing multiple approval stages across consultants, clients, and contractors. Practicio keeps every decision documented and every sign-off on record."
    },
    {
      title: "Interior Designers",
      description: "Client choices change constantly. Material selections, layout revisions, finish updates — all tracked, timestamped, and tied to approvals."
    },
    {
      title: "Design Consultants",
      description: "Your credibility depends on clear communication. Practicio gives every engagement a structured record from brief to handover."
    },
    {
      title: "Small Studios",
      description: "Professional project control without hiring a project manager. Set up in a day, running within a week."
    }
  ]
};

export const positioningSection = {
  badge: "Not a CRM",
  headline: "Not a CRM. Not a task manager. Not another tool your team won't use.",
  paragraphs: [
    "Practicio is built for one thing: giving architects and interior designers in India a structured way to run their projects — from the first client meeting to the final invoice. Everything else is noise."
  ]
};

export const ctaSection = {
  headline: "Ready to run tighter projects?",
  subheadline: "Join architects and interior designers across Mumbai, Bengaluru, Pune, and Delhi who are moving off WhatsApp and Excel.",
  primaryCTA: "Book a Demo",
  secondaryCTA: "View Pricing",
  disclaimer: "It's 20 minutes, no commitment"
};

export const faqSection = {
  headline: "Frequently asked questions",
  subheadline: "Everything you need to know before getting started.",
  initialVisible: 5,
  showMoreLabel: "Show more questions",
  showLessLabel: "Show fewer questions",
  items: [
    {
      question: "Why would I add another tool? Won't that just increase my admin work?",
      answer: "Every tool feels like more work before you use it. But right now your admin is already happening — it's just scattered across WhatsApp, email, Excel, and your memory. Practicio doesn't add admin. It consolidates what you're already doing into one place, so it takes less time, not more. Most designers tell us the first thing they notice is that they stop spending time looking for things."
    },
    {
      question: "I already use Excel and WhatsApp. Why change now?",
      answer: "Excel and WhatsApp work — until they don't. A missed approval, a scope dispute, an invoice that slipped. Practicio doesn't replace how you think about projects. It removes the admin that costs you time and money between the cracks."
    },
    {
      question: "How long does it take to set up?",
      answer: "Less than an hour to get your first project live. Most studios are running within a day. There are no training videos, no onboarding decks — if you can use WhatsApp, you can use Practicio."
    },
    {
      question: "What if my client won't use yet another tool?",
      answer: "Your client doesn't need an account. They receive a simple link, see exactly what needs their approval, and click approve or reject. That's it. No app, no login, no learning curve on their end."
    },
    {
      question: "Is there a free trial?",
      answer: "Yes — all plans include a 14-day money-back guarantee. No risk to try it on a live project."
    },
    {
      question: "Is this only useful for large studios with big teams?",
      answer: "No — most of our early users are solo architects and 2–3 person studios. Practicio is designed to be useful from your very first project, whether you're managing one project or ten."
    },
    {
      question: "My team is resistant to new tools. Will they actually adopt this?",
      answer: "That's the most common concern we hear. Practicio is built to be picked up without training. Most teams are using it independently within a week. And if you hit any friction in the first 30 days, we'll do an onboarding call with your team personally."
    },
    {
      question: "What does ₹2,998/month actually get me?",
      answer: "Unlimited projects, unlimited proposals, up to 10 team members, client portal, BOQ tracking, PDF generation, proposal versioning, change orders, audit log, and no watermarks on exports. For a solo practice running 2–3 projects at a time, one avoided scope dispute pays for a year of Practicio."
    }
  ]
};

export const footerContent = {
  tagline: "Built for architects and interior designers in India.",
  email: "hello@practicio.io",
  sections: [
    {
      title: "Product",
      links: [
        { label: "Features", href: "#features" },
        { label: "Pricing", href: "#pricing" },
        { label: "Updates", href: "#" }
      ]
    },
    {
      title: "Company",
      links: [
        { label: "About", href: "/about" },
        { label: "Contact", href: "#contact" },
        { label: "Careers", href: "#" }
      ]
    }
  ],
  legal: [
    { label: "Privacy", href: "/privacy" },
    { label: "Terms", href: "/terms" },
    { label: "Cookie Policy", href: "/cookie-policy" }
  ],
  copyright: "© 2026 Practicio. All rights reserved."
};

export const navigationLinks = [
  { label: "Features", href: "features" },
  { label: "Pricing", href: "pricing" },
  { label: "Who It's For", href: "who-its-for" },
  { label: "Contact", href: "contact" }
];